import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Toolbar,
    useTheme,   // 追加
} from '@mui/material';
import {
    AddCircleOutline,
    Assessment,
    Calculate,
    ExpandLess,
    ExpandMore,
    FolderOpen,
    SignalCellularAlt,
    ShowChart,
    Vibration,
    ExitToAppOutlined,
} from '@mui/icons-material';
import { Resizable } from 're-resizable';
import { useAtomValue } from 'jotai';
import { alpha } from '@mui/material/styles';
import { powertrainAtom } from 'atoms/powertrainAtom';
import { sideNavOpenAtom } from 'atoms/sideNavAtom';
import { POWERTRAIN_COLORS } from 'styles/theme';

type MenuItem = {
    text: string;
    icon: React.ReactNode;
    path?: string;
    children?: MenuItem[];
};

const menuItems: MenuItem[] = [
    {
        text: 'R 検討ツール',
        icon: <Assessment />,
        path: '/r-tools',
        children: [
            {
                text: '新規計算セット作成',
                icon: <AddCircleOutline />,
                path: '/r-tools/new-project',
            },
            {
                text: 'データ登録',
                icon: <ExitToAppOutlined />,
                path: '/r-tools/data-registration',
            },
            {
                text: '計算セット選択',
                icon: <FolderOpen />,
                path: '/r-tools/existing-project',
            },
            {
                text: '計算',
                icon: <Calculate />,
                path: '/r-tools/calculation',
            },
            {
                text: '室内音計算モード',
                icon: <ShowChart />,
                path: '/r-tools/cabin-noise',
            },
            {
                text: 'MTG先端振動計算モード',
                icon: <Vibration />,
                path: '/r-tools/mtg-vib',
            },
        ],
    },
    {
        text: 'L 検討ツール',
        icon: <SignalCellularAlt />,
        path: '/l-tools',
        children: [
            {
                text: '新規計算セット作成',
                icon: <AddCircleOutline />,
                path: '/l-tools/new-project',
            },
            {
                text: 'データ登録',
                icon: <ExitToAppOutlined />,
                path: '/l-tools/data-registration',
            },
            {
                text: '計算セット選択',
                icon: <FolderOpen />,
                path: '/l-tools/existing-project',
            },
            {
                text: '計算',
                icon: <Calculate />,
                path: '/l-tools/calculation',
            },
            {
                text: '達成度判断表',
                icon: <ShowChart />,
                path: '/l-tools/matrix',
            },
        ],
    },
];

type Props = {
    width: number;
    onWidthChange: (w: number) => void;
};

export const SideNav: React.FC<Props> = ({ width, onWidthChange }) => {
    const theme = useTheme();
    const open = useAtomValue(sideNavOpenAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const pc = POWERTRAIN_COLORS[powertrain];
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [openMap, setOpenMap] = useState<Record<string, boolean>>(() =>
        menuItems.reduce((acc, item) => {
            if (item.children?.some(c => c.path && pathname.startsWith(c.path)))
                acc[item.path ?? item.text] = true;
            return acc;
        }, {} as Record<string, boolean>)
    );

    const toggle = (k: string) => setOpenMap(p => ({ ...p, [k]: !p[k] }));

    const render = (items: MenuItem[], depth = 0) =>
        items.map(it => {
            const key = it.path ?? it.text;
            const hasChild = !!it.children?.length;
            const open = !!openMap[key];

            return (
                <React.Fragment key={key}>
                    <ListItemButton
                        sx={{
                            // インセットの角丸ピル。両端に一定マージンを置くことで選択時も横ずれしない。
                            mx: 1,
                            my: 0.25,
                            borderRadius: 2,
                            pl: 1.5 + depth * 2,
                            // 選択中の項目を powertrain 差し色（BEV=ティール / HEV=パープル）で示す。
                            '&.Mui-selected': {
                                color: pc.main,
                                bgcolor: alpha(pc.main, 0.12),
                                '& .MuiListItemIcon-root': { color: pc.main },
                                '&:hover': { bgcolor: alpha(pc.main, 0.18) },
                            },
                        }}
                        selected={!hasChild && pathname === it.path}
                        onClick={() => {
                            if (hasChild) toggle(key);
                            else if (it.path) navigate(it.path);
                        }}
                    >
                        <ListItemIcon>{it.icon}</ListItemIcon>
                        <ListItemText primary={it.text} />
                        {hasChild && (open ? <ExpandLess /> : <ExpandMore />)}
                    </ListItemButton>

                    {hasChild && (
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <List disablePadding>{render(it.children!, depth + 1)}</List>
                        </Collapse>
                    )}
                </React.Fragment>
            );
        });

    // 閉じている間は非表示。幅(navWidth)は親(MainLayout)が保持するため再表示で復元される。
    if (!open) return null;

    return (
        <Resizable
            size={{ width, height: '100%' }}
            minWidth={200}
            maxWidth={400}
            enable={{ right: true }}
            /* 右端 8 px をハンドルにする（Drawer より前面） */
            handleStyles={{
                right: {
                    width: 8,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    cursor: 'col-resize',
                    position: 'absolute',
                    zIndex: theme.zIndex.drawer + 1,
                },
            }}
            /* ドラッグ終了時だけ幅を確定 */
            onResizeStop={(_, __, ref) => onWidthChange(ref.offsetWidth)}
            style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}
        >
            <Drawer
                variant="permanent"
                sx={{
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: `${width}px`,
                        boxSizing: 'border-box',
                    },
                }}
            >
                <Toolbar />
                {/* navigation landmark。ワークフローステッパーと同名ボタンを区別できるよう命名する。 */}
                <List component="nav" aria-label="メインメニュー" disablePadding>{render(menuItems)}</List>
            </Drawer>
        </Resizable>
    );
};
